/**
 * Deserialize markdown string to Slate node tree.
 */
import { marked } from 'marked';

const EMPTY_PARAGRAPH = { type: 'paragraph', children: [{ text: '' }] };

/**
 * Convert marked inline tokens to Slate leaf/inline nodes.
 */
const deserializeInlineTokens = (tokens) => {
  if (!tokens || tokens.length === 0) return [{ text: '' }];

  const result = [];

  for (const token of tokens) {
    switch (token.type) {
      case 'text': {
        if (token.tokens && token.tokens.length > 0) {
          result.push(...deserializeInlineTokens(token.tokens));
        } else {
          result.push({ text: token.text || '' });
        }
        break;
      }
      case 'strong': {
        const children = deserializeInlineTokens(token.tokens);
        for (const child of children) {
          if ('text' in child) {
            result.push({ ...child, bold: true });
          } else {
            result.push(child);
          }
        }
        break;
      }
      case 'em': {
        const children = deserializeInlineTokens(token.tokens);
        for (const child of children) {
          if ('text' in child) {
            result.push({ ...child, italic: true });
          } else {
            result.push(child);
          }
        }
        break;
      }
      case 'codespan': {
        result.push({ text: token.text || '', code: true });
        break;
      }
      case 'link': {
        result.push({
          type: 'link',
          url: token.href || '',
          children: token.tokens
            ? deserializeInlineTokens(token.tokens)
            : [{ text: token.text || '' }],
        });
        break;
      }
      case 'image': {
        result.push({
          type: 'image',
          url: token.href || '',
          alt: token.text || '',
          children: [{ text: '' }],
        });
        break;
      }
      case 'br': {
        result.push({ text: '\n' });
        break;
      }
      default: {
        result.push({ text: token.raw || token.text || '' });
        break;
      }
    }
  }

  return result.length > 0 ? result : [{ text: '' }];
};

/**
 * Convert a block-level marked token to a Slate node.
 */
const deserializeBlockToken = (token) => {
  switch (token.type) {
    case 'heading': {
      const type = token.depth <= 1 ? 'heading-one' : 'heading-two';
      return { type, children: deserializeInlineTokens(token.tokens) };
    }

    case 'paragraph': {
      return { type: 'paragraph', children: deserializeInlineTokens(token.tokens) };
    }

    case 'blockquote': {
      const innerTokens = token.tokens || [];
      let children = [];
      for (const sub of innerTokens) {
        if (sub.type === 'paragraph' && sub.tokens) {
          if (children.length > 0) children.push({ text: '\n' });
          children.push(...deserializeInlineTokens(sub.tokens));
        }
      }
      return {
        type: 'block-quote',
        children: children.length > 0 ? children : [{ text: '' }],
      };
    }

    case 'list': {
      const listType = token.ordered ? 'numbered-list' : 'bulleted-list';
      return {
        type: listType,
        children: (token.items || []).map((item) => {
          let itemChildren = [{ text: '' }];
          if (item.tokens && item.tokens.length > 0) {
            const first = item.tokens[0];
            if (first.tokens) {
              itemChildren = deserializeInlineTokens(first.tokens);
            } else {
              itemChildren = [{ text: item.text || '' }];
            }
          }
          return { type: 'list-item', children: itemChildren };
        }),
      };
    }

    case 'table': {
      const rows = [];
      if (token.header) {
        rows.push({
          type: 'table-row',
          children: token.header.map((cell) => ({
            type: 'table-cell',
            children: cell.tokens
              ? deserializeInlineTokens(cell.tokens)
              : [{ text: cell.text || '' }],
          })),
        });
      }
      if (token.rows) {
        for (const row of token.rows) {
          rows.push({
            type: 'table-row',
            children: row.map((cell) => ({
              type: 'table-cell',
              children: cell.tokens
                ? deserializeInlineTokens(cell.tokens)
                : [{ text: cell.text || '' }],
            })),
          });
        }
      }
      if (rows.length === 0) {
        rows.push({
          type: 'table-row',
          children: [{ type: 'table-cell', children: [{ text: '' }] }],
        });
      }
      return { type: 'table', children: rows };
    }

    case 'code': {
      return { type: 'paragraph', children: [{ text: token.text || '', code: true }] };
    }

    case 'hr': {
      return { type: 'paragraph', children: [{ text: '---' }] };
    }

    case 'space':
      return null;

    default:
      return { type: 'paragraph', children: [{ text: token.raw || token.text || '' }] };
  }
};

/**
 * Deserialize a markdown string into an array of Slate nodes.
 */
export const deserializeMarkdown = (markdown) => {
  if (!markdown || typeof markdown !== 'string' || !markdown.trim()) {
    return [{ ...EMPTY_PARAGRAPH }];
  }

  const tokens = marked.lexer(markdown);
  const nodes = [];

  for (const token of tokens) {
    const node = deserializeBlockToken(token);
    if (node) nodes.push(node);
  }

  return nodes.length > 0 ? nodes : [{ ...EMPTY_PARAGRAPH }];
};
